"use client";

import type React from "react"
import { Inter } from "next/font/google"
import "./globals.css"
import { ThemeProvider } from "@/components/theme-provider"
import Navigation from "@/components/navigation"
import { AppHeader } from "@/components/app-header"
import { AuthProvider } from "@/components/auth-provider"
import { AuthGuard } from "@/components/auth-guard"
import { SessionGuardGate } from "@/components/session-guard-gate"
import { CompanyProvider } from "@/contexts/CompanyContext"
import { NotificationProvider } from "@/contexts/NotificationContext"
import { usePathname } from "next/navigation"

const inter = Inter({ subsets: ["latin"] })

export default function RootLayout({
  children,
}: Readonly<{
  children: React.ReactNode
}>) {
  const pathname = usePathname();
  const isLoginPage = pathname.includes('/login');
  const isLogoutPage = pathname.includes('/auth/logout');
  // /auth/import は SSO bridge 用。AuthGuard 通さずに token を localStorage に保存させる
  const isImportPage = pathname.includes('/auth/import');

  return (
    <html lang="ja" suppressHydrationWarning>
      <head>
        <title>スタッフマイページ</title>
        <meta name="description" content="シフト管理、メッセージ連携、行動ログの可視化" />
        {/* Web Push(iOSは「ホーム画面に追加」したPWAのみ対応)のためのPWAマニフェスト */}
        <link rel="manifest" href="/staff/manifest.json" />
        {/* iOSでホーム画面起動時にstandalone(ブラウザUIなし)にする */}
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      </head>
      <body className={inter.className}>
        <AuthProvider>
          <CompanyProvider>
            <NotificationProvider>
              <ThemeProvider attribute="class" defaultTheme="light" enableSystem disableTransitionOnChange>
                <div className="flex flex-col min-h-screen">
                  {isLoginPage || isLogoutPage || isImportPage ? (
                    <main className="flex-1">{children}</main>
                  ) : (
                    <AuthGuard>
                      <SessionGuardGate>
                        <AppHeader />
                        <main className="flex-1 flex flex-col">{children}</main>
                        <Navigation />
                      </SessionGuardGate>
                    </AuthGuard>
                  )}
                </div>
              </ThemeProvider>
            </NotificationProvider>
          </CompanyProvider>
        </AuthProvider>
      </body>
    </html>
  )
}
